import axios from "axios";

interface ApiErrorBody {
  message?: string;
  error?: string;
  code?: string;
}

interface ApiErrorResponse {
  status?: number;
  data?: ApiErrorBody;
}

/**
 * Reads the HTTP response off an error thrown by `apiClient` or
 * `portalApiClient`.
 *
 * Both clients' response interceptors rewrite any error whose body carries
 * `message`/`error` into a PLAIN `Error` (copying `.response` across) — and
 * since the backend's `errorResponse()` always sends `{ code, message }`,
 * that covers essentially every backend error. The practical consequence:
 *
 *     axios.isAxiosError(error)  // ALWAYS false for backend errors
 *
 * so any status-code branch guarded by `axios.isAxiosError(...)` or
 * `error instanceof AxiosError` is dead code, and the UI silently falls
 * through to generic copy (the OBS-108 production bug) or mis-classifies
 * retryable failures.
 *
 * Always classify API errors through these helpers instead of gating on
 * `axios.isAxiosError` / `instanceof AxiosError`. Genuine network failures
 * (no response at all) still arrive as real AxiosErrors and are handled here
 * too.
 *
 * @see frontend/src/lib/apiClient.ts
 * @see frontend/src/lib/portalApiClient.ts
 */
export function getApiErrorResponse(
  error: unknown
): ApiErrorResponse | undefined {
  if (axios.isAxiosError(error)) {
    return error.response;
  }
  return (error as { response?: ApiErrorResponse } | null | undefined)
    ?.response;
}

/**
 * HTTP status of a failed API call, or `undefined` when the request never
 * got a response (network failure, timeout, CORS) — use that distinction
 * rather than assuming a status is always present.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = getApiErrorResponse(error)?.status;
  return typeof status === "number" ? status : undefined;
}

/** True when the failed call matches any of the given HTTP statuses. */
export function isApiErrorStatus(
  error: unknown,
  ...statuses: number[]
): boolean {
  const status = getApiErrorStatus(error);
  return status !== undefined && statuses.includes(status);
}

/** True when a response came back at all (i.e. not a network-level failure). */
export function hasApiErrorResponse(error: unknown): boolean {
  return getApiErrorStatus(error) !== undefined;
}

/**
 * The backend's own user-facing message, already sanitized server-side by
 * `errorHandler.ts`. Prefer this over hardcoded copy so users see the real
 * reason. Returns `undefined` when the body has no message — including for
 * `responseType: "blob"` requests, whose error bodies are Blobs rather than
 * parsed JSON.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  const data = getApiErrorResponse(error)?.data;
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const message = data.message || data.error;
  return typeof message === "string" && message ? message : undefined;
}

/** The backend's machine-readable error code (e.g. "ConflictError"). */
export function getApiErrorCode(error: unknown): string | undefined {
  const code =
    getApiErrorResponse(error)?.data?.code ??
    (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
}
